import { FC, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux-hooks';
import { setToastInfo } from '../../../store/common.slice';
import { ToastType } from '../../../types/common.types';
import { ArrowLeft } from 'lucide-react';
import { Form, Formik } from 'formik';
import { inputFieldStyles, textareaStyles } from '../../../global-styles';
import ManageUsers from '../../common/ManageUsers';
import Button from '../../common/Button';
import { ButtonVariant } from '../../../types/button.enum';
import QuizService from '../../../services/quiz.service';
import { useNavigate, useParams } from 'react-router-dom';
import { UpdateQuizInput } from './types';
import SkillsOutcomesSelector from '../grading/SkillsOutcomesSelector';
import ManageQuizFiles from './ManageQuizFiles';
import { LabelWithTooltip } from '../../common/Tooltip';
import UploadQuizFiles from './UploadQuizFiles';
import QuizScrapeUrlDialog from './QuizScrapeUrlDialog';
import Switch from '../../common/Switch';
import QuizContent from './QuizContent';
import { UserPromtDialog } from '../../common/UserPromtDialog';
import IconButton, { IconButtonTypes } from '../../common/IconButton';
import ManageUsersandGroups from '../../common/ManageUsers&Groups';

const QUESTION_TYPES = [
  'multiple choice',
  'true false',
  'short answer',
  'essay',
  'fill in the blank',
  'objective',
  'subjective',
];

const UpdateQuizSchema = Yup.object().shape({
  _id: Yup.string().required(),
  name: Yup.string().required('Name is required.').min(1).max(300),
  skills: Yup.array().of(Yup.string()).min(1, 'At least one skill is required'),
  learningOutcomes: Yup.array()
    .of(Yup.string())
    .min(1, 'At least one learning outcome is required'),
  description: Yup.string().max(400),
  image: Yup.string(),
  allowedUserIds: Yup.array().of(Yup.string()).default([]),
  allowedGroupIds: Yup.array().of(Yup.string()).default([]),
  numAttempts: Yup.number().min(1, 'At least 1 attempt required').default(1),
  numQuestions: Yup.number().min(0).default(0),
  timeLimit: Yup.number().min(1, 'At least 1 minute required'),
  deadline: Yup.string(),
  questionType: Yup.string()
    .oneOf(QUESTION_TYPES, 'Invalid question type')
    .required('Question type is required'),
  filesData: Yup.array().of(Yup.mixed()).default([]),

  scrapedUrls: Yup.array().of(Yup.string().url()).default([]),
  extractImages: Yup.boolean().default(false),
});

const ManageQuiz: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { quizId } = useParams();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [testContent, setTestContent] = useState<string[]>([]);
  const [quizStatus, setQuizStatus] = useState<string>('pending');
  const [showScrapeDialog, setShowScrapeDialog] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [initialValues, setInitialValues] = useState<UpdateQuizInput | null>(
    null
  );
  const { orgInfo } = useAppSelector((state) => state.common);
  const [selectedTab, setSelectedTab] = useState<'files' | 'content'>('files');

  const fetchQuiz = async (id: string) => {
    try {
      const response = await QuizService.getQuizById(id);
      if (response.data.data?.quiz) {
        const quiz = response.data.data.quiz;
        setTestContent(quiz.testContent || []);
        setQuizStatus(quiz.status || 'pending');
        setInitialValues({
          _id: quiz._id as string,
          name: quiz.name,
          skills: quiz.skills || [],
          learningOutcomes: quiz.learningOutcomes || [],
          description: quiz.description || '',
          image: quiz.image || '',
          allowedUserIds: quiz.allowedUserIds || [],
          allowedGroupIds: quiz.allowedGroupIds || [],
          numAttempts: quiz.numAttempts || 1,
          numQuestions: quiz.numQuestions || 0,
          timeLimit: quiz.timeLimit || 0,
          questionType: quiz.questionType,
          deadline: quiz.deadline
            ? (() => {
                const d = new Date(quiz.deadline);
                const pad = (n: number) => String(n).padStart(2, '0');
                return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
              })()
            : '',
          filesData: quiz.filesData || [],
          scrapedUrls:
            quiz.scrapedUrls && quiz.scrapedUrls.length > 0
              ? quiz.scrapedUrls.map((url: string) =>
                  /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(url)
                    ? url
                    : `https://${url}`
                )
              : [],
          extractImages: quiz.extractImages || false,
          userId: quiz.userId,
        });
      } else {
        dispatch(
          setToastInfo({
            isOpen: true,
            message: 'Error in fetching quiz.',
            type: ToastType.ERROR,
          })
        );
      }
    } catch (err: any) {
      dispatch(
        setToastInfo({
          isOpen: true,
          message: err?.response?.data?.message || 'Error fetching quiz',
          type: ToastType.ERROR,
        })
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (quizId) {
      fetchQuiz(quizId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizId]);

  const updateQuiz = async (values: UpdateQuizInput) => {
    try {
      const payload = {
        ...values,
        deadline: values.deadline
          ? new Date(values.deadline).toISOString()
          : '',
      };
      const response = await QuizService.updateQuiz(payload);
      if (response.data.data?.quiz) {
        dispatch(
          setToastInfo({
            isOpen: true,
            message: response.data.message || 'Quiz updated successfully',
            type: ToastType.SUCCESS,
          })
        );
        // Refresh the data to reset dirty state
        await fetchQuiz(quizId!);
      } else {
        dispatch(
          setToastInfo({
            isOpen: true,
            message: response.data.message || 'Error updating quiz',
            type: ToastType.ERROR,
          })
        );
      }
    } catch (err: any) {
      dispatch(
        setToastInfo({
          isOpen: true,
          message: err?.response?.data?.message || 'Error updating quiz',
          type: ToastType.ERROR,
        })
      );
    }
  };

  const updateQuizUsers = async (
    allowedUserIds: string[],
    allowedGroupIds: string[]
  ) => {
    try {
      const response = await QuizService.updateQuizUsers(
        quizId!,
        allowedUserIds,
        allowedGroupIds
      );
      if (response.data.data?.quiz) {
        dispatch(
          setToastInfo({
            isOpen: true,
            message: response.data.message || 'Quiz users updated successfully',
            type: ToastType.SUCCESS,
          })
        );
        await fetchQuiz(quizId!);
      } else {
        dispatch(
          setToastInfo({
            isOpen: true,
            message: response.data.message || 'Error updating quiz users',
            type: ToastType.ERROR,
          })
        );
      }
    } catch (err: any) {
      dispatch(
        setToastInfo({
          isOpen: true,
          message: err?.response?.data?.message || 'Error updating quiz users',
          type: ToastType.ERROR,
        })
      );
    }
  };

  const handleGenerateQuiz = async () => {
    if (!quizId) return;
    setGenerating(true);
    try {
      const response = await QuizService.generateQuiz(quizId);
      if (response.data.data?.quiz) {
        const quiz = response.data.data.quiz;
        setTestContent(quiz.testContent || []);
        dispatch(
          setToastInfo({
            isOpen: true,
            message: response.data.message || 'Quiz generated successfully',
            type: ToastType.SUCCESS,
          })
        );
      } else {
        dispatch(
          setToastInfo({
            isOpen: true,
            message: response.data.message || 'Error generating quiz',
            type: ToastType.ERROR,
          })
        );
      }
    } catch (err: any) {
      dispatch(
        setToastInfo({
          isOpen: true,
          message: err?.response?.data?.message || 'Error generating quiz',
          type: ToastType.ERROR,
        })
      );
    } finally {
      setGenerating(false);
    }
  };

  const handlePublishQuiz = async () => {
    if (!quizId) return;
    setShowPublishDialog(false);
    setPublishing(true);
    try {
      const response = await QuizService.publishQuiz(quizId);
      if (response.data.data?.quiz) {
        setQuizStatus(response.data.data.quiz.status || 'active');
        dispatch(
          setToastInfo({
            isOpen: true,
            message: response.data.message || 'Quiz published successfully',
            type: ToastType.SUCCESS,
          })
        );
      } else {
        dispatch(
          setToastInfo({
            isOpen: true,
            message: response.data.message || 'Error publishing quiz',
            type: ToastType.ERROR,
          })
        );
      }
    } catch (err: any) {
      dispatch(
        setToastInfo({
          isOpen: true,
          message: err?.response?.data?.message || 'Error publishing quiz',
          type: ToastType.ERROR,
        })
      );
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#020817] mx-auto mb-4"></div>
          <p className="text-[#666]">Loading quiz...</p>
        </div>
      </div>
    );
  }

  if (!initialValues) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">Quiz not found</p>
          <Button
            onClick={() => navigate('/quiz')}
            variant={ButtonVariant.PRIMARY}
          >
            Back to Quizzes
          </Button>
        </div>
      </div>
    );
  }

  const isPublished = quizStatus === 'active';

  return (
    <div className="w-full">
      <div className="flex flex-col flex-grow h-full">
        <div className="flex items-center justify-between mb-4 px-5 py-3 border-b border-[#E2E8F0]">
          <div className="flex items-center gap-2">
            <ArrowLeft
              color="black"
              size={20}
              onClick={() => navigate('/quiz')}
              className="cursor-pointer"
            />
            <h3 className="text-[24px] font-semibold">Edit Quiz</h3>
            {isPublished ? (
              <span className="text-[11px] font-medium text-[#22c55e] bg-[#f0fdf4] px-2.5 py-1 rounded-full border border-[#22c55e]">
                Published
              </span>
            ) : (
              <span className="text-[11px] font-medium text-[#f59e0b] bg-[#fffbeb] px-2.5 py-1 rounded-full border border-[#f59e0b]">
                DRAFT
              </span>
            )}
          </div>
          <IconButton
            icon=""
            text="View Metrics"
            color={IconButtonTypes.PRIMARY}
            type="button"
            classes="min-w-[150px]"
            onClick={() =>
              navigate('metrics', {
                state: { quiz: { name: initialValues.name, _id: quizId } },
              })
            }
          />
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={UpdateQuizSchema}
          validateOnChange={true}
          enableReinitialize={true}
          onSubmit={async (values) => {
            if (isPublished) {
              await updateQuizUsers(
                values.allowedUserIds as string[],
                values.allowedGroupIds as string[]
              );
            } else {
              await updateQuiz(values);
            }
          }}
        >
          {({
            handleChange,
            handleSubmit,
            handleBlur,
            isValid,
            values,
            setFieldValue,
            errors,
            touched,
            dirty,
          }) => {
            return (
              <Form
                onSubmit={handleSubmit}
                className="flex flex-col justify-between"
              >
                <div className="flex px-5 max-h-[calc(100vh-160px)]">
                  <div className="flex flex-col gap-4 w-[55%] pr-2.5 overflow-y-auto">
                    <fieldset
                      disabled={isPublished}
                      className={isPublished ? 'opacity-60' : ''}
                    >
                      <div className="flex flex-col gap-4">
                        <div>
                          <label className="text-[13px] font-medium text-[#020817] mb-2 inline-block">
                            <LabelWithTooltip
                              label="Quiz Name *"
                              tooltip="Give your quiz a descriptive name so you can easily identify it later."
                              direction="right"
                            />
                          </label>
                          <input
                            type="text"
                            name="name"
                            className={inputFieldStyles}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.name}
                          />
                          {touched.name && errors.name && (
                            <p className="text-red-500 text-[12px]">
                              {errors.name}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="text-[13px] font-medium text-[#020817] mb-2 inline-block">
                            <LabelWithTooltip
                              label="Description"
                              tooltip="A brief description of the quiz."
                              direction="right"
                            />
                          </label>
                          <textarea
                            name="description"
                            className={`${textareaStyles} min-h-[80px] resize-none`}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.description}
                            placeholder="Enter quiz description..."
                          />
                          {touched.description && errors.description && (
                            <p className="text-red-500 text-[12px]">
                              {errors.description}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="text-[13px] font-medium text-[#020817] mb-2 inline-block">
                            <LabelWithTooltip
                              label="Skills & Learning Outcomes *"
                              tooltip="Select the skills this quiz will assess and the learning outcomes students should achieve."
                              direction="right"
                            />
                          </label>
                          <SkillsOutcomesSelector
                            selectedSkills={values.skills}
                            selectedOutcomes={values.learningOutcomes}
                            onSkillsChange={(skills) =>
                              setFieldValue('skills', skills)
                            }
                            onOutcomesChange={(outcomes) =>
                              setFieldValue('learningOutcomes', outcomes)
                            }
                          />
                          {(touched.skills && errors.skills) ||
                          (touched.learningOutcomes &&
                            errors.learningOutcomes) ? (
                            <div className="text-red-500 text-[12px] mt-1">
                              {errors.skills || errors.learningOutcomes}
                            </div>
                          ) : null}
                        </div>

                        <div>
                          <label className="text-[13px] font-medium text-[#020817] mb-2 inline-block">
                            <LabelWithTooltip
                              label="Question Type *"
                              tooltip="Select the type of questions for this quiz."
                              direction="right"
                            />
                          </label>
                          <select
                            name="questionType"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.questionType}
                            className={inputFieldStyles}
                          >
                            {QUESTION_TYPES.map((type) => (
                              <option key={type} value={type}>
                                {type.charAt(0).toUpperCase() + type.slice(1)}
                              </option>
                            ))}
                          </select>
                          {touched.questionType && errors.questionType && (
                            <p className="text-red-500 text-[12px]">
                              {errors.questionType}
                            </p>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-[13px] font-medium text-[#020817] mb-2 inline-block">
                              <LabelWithTooltip
                                label="Number of Questions"
                                tooltip="How many questions this quiz should contain."
                                direction="right"
                              />
                            </label>
                            <input
                              type="number"
                              name="numQuestions"
                              min={0}
                              className={inputFieldStyles}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.numQuestions}
                            />
                          </div>
                          <div>
                            <label className="text-[13px] font-medium text-[#020817] mb-2 inline-block">
                              <LabelWithTooltip
                                label="Number of Attempts"
                                tooltip="How many times a student can attempt this quiz."
                                direction="right"
                              />
                            </label>
                            <input
                              type="number"
                              name="numAttempts"
                              min={1}
                              className={inputFieldStyles}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.numAttempts}
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-[13px] font-medium text-[#020817] mb-2 inline-block">
                              <LabelWithTooltip
                                label="Time Limit (minutes)"
                                tooltip="Time limit for the quiz in minutes. Set 0 for no limit."
                                direction="right"
                              />
                            </label>
                            <input
                              type="number"
                              name="timeLimit"
                              min={0}
                              className={inputFieldStyles}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.timeLimit}
                            />
                          </div>
                          <div>
                            <label className="text-[13px] font-medium text-[#020817] mb-2 inline-block">
                              <LabelWithTooltip
                                label="Deadline"
                                tooltip="Set a deadline for quiz completion."
                                direction="right"
                              />
                            </label>
                            <input
                              type="datetime-local"
                              name="deadline"
                              className={inputFieldStyles}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.deadline}
                            />
                          </div>
                        </div>

                        {orgInfo?.extractImages && (
                          <div className="pb-8 flex items-center justify-between space-x-3">
                            <div className="">
                              <Switch
                                label="Allow Image Extraction"
                                handleClick={() => {
                                  setFieldValue(
                                    'extractImages',
                                    !values.extractImages
                                  );
                                }}
                                disabled={orgInfo.extractImages ? false : true}
                                active={values.extractImages as boolean}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </fieldset>

                    {/* ManageUsers stays editable even after publishing */}
                    <div className="mb-5">
                      <ManageUsersandGroups
                        onUserSelect={(selectedUserIds) => {
                          setFieldValue('allowedUserIds', selectedUserIds);
                        }}
                        onGroupSelect={(selectedGroupIds) => {
                          setFieldValue('allowedGroupIds', selectedGroupIds);
                        }}
                        selectedUserIds={values.allowedUserIds || []}
                        selectedGroupIds={values.allowedGroupIds || []}
                      />
                    </div>
                  </div>

                  <div className={`w-[45%] pl-2.5 overflow-y-auto`}>
                    <div className="flex items-center border-b-[1px] border-[#ccc] mb-3">
                      <div
                        className={`
                          cursor-pointer px-5 py-2 font-medium text-[14px]
                          ${selectedTab === 'files' ? 'border-b-[3px] border-blue-500' : ''}
                        `}
                        onClick={() => setSelectedTab('files')}
                      >
                        Files/Urls
                      </div>
                      <div
                        className={`
                          cursor-pointer px-5 py-2 font-medium text-[14px]
                          ${selectedTab === 'content' ? 'border-b-[3px] border-blue-500' : ''}
                        `}
                        onClick={() => setSelectedTab('content')}
                      >
                        Quiz Content
                      </div>
                    </div>
                    {selectedTab === 'files' && (
                      <div className="flex flex-col gap-4">
                        <div>
                          <label className="text-[13px] font-medium text-[#020817] mb-2 inline-block">
                            <LabelWithTooltip
                              label="Upload Files & Scrape URLs"
                              tooltip="Add knowledge sources for the quiz by uploading files or scraping URLs."
                              direction="left"
                            />
                          </label>
                          <div
                            className={`flex gap-2  ${isPublished ? 'opacity-80 pointer-events-none' : ''}`}
                          >
                            <div className="flex-1">
                              <UploadQuizFiles
                                onClose={() => fetchQuiz(quizId!)}
                                filesData={values.filesData}
                                quizId={quizId}
                                extractImages={values.extractImages}
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowScrapeDialog(true)}
                              className={`${inputFieldStyles} flex items-center flex-1`}
                            >
                              <i className="icon-plus text-xs" />
                              <span className="ml-2">Scrape URL</span>
                            </button>
                          </div>
                        </div>

                        <div>
                          <label className="text-[13px] font-medium text-[#020817] mb-2 inline-block">
                            <LabelWithTooltip
                              label="Manage Files"
                              tooltip="View and manage uploaded files and scraped URLs for this quiz."
                              direction="left"
                            />
                          </label>
                          <ManageQuizFiles
                            filesData={values.filesData || []}
                            scrapedUrls={values.scrapedUrls || []}
                            refreshFilesData={() => fetchQuiz(quizId!)}
                            readOnly={isPublished}
                          />
                        </div>
                      </div>
                    )}
                    {selectedTab === 'content' && testContent.length > 0 && (
                      <div className="pb-4 pr-3">
                        <QuizContent
                          testContent={testContent}
                          questionType={values.questionType}
                          quizId={quizId!}
                          onUpdate={(updated) => setTestContent(updated)}
                          readOnly={isPublished}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex pt-4 pb-2 border-t border-[#E2E8F0] px-5 justify-between gap-3">
                  {!isPublished && (
                    <Button
                      onClick={handleGenerateQuiz}
                      disabled={generating}
                      variant={ButtonVariant.PRIMARY}
                      type="button"
                    >
                      {testContent.length === 0
                        ? generating
                          ? 'Generating...'
                          : 'Generate Quiz'
                        : 'Regenerate Quiz'}
                    </Button>
                  )}
                  <div className="flex items-center gap-2 ml-auto">
                    {!isPublished && testContent.length > 0 && (
                      <Button
                        onClick={() => setShowPublishDialog(true)}
                        disabled={publishing}
                        variant={ButtonVariant.PRIMARY}
                        type="button"
                        classes="bg-[#22c55e] hover:bg-[#16a34a]"
                      >
                        {publishing ? 'Publishing...' : 'Publish'}
                      </Button>
                    )}
                    <Button
                      type="submit"
                      disabled={!dirty || !isValid}
                      variant={ButtonVariant.PRIMARY}
                    >
                      {isPublished ? 'Update Users' : 'Save Changes'}
                    </Button>
                    <Button
                      onClick={() => navigate('/quiz')}
                      variant={ButtonVariant.SECONDARY}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>

      {showScrapeDialog && quizId && (
        <QuizScrapeUrlDialog
          quizId={quizId}
          onClose={() => {
            setShowScrapeDialog(false);
            fetchQuiz(quizId);
          }}
        />
      )}

      {showPublishDialog && (
        <UserPromtDialog
          title="Publish Quiz"
          message="Are you sure you want to publish this quiz? Once published, the quiz cannot be edited."
          cancelHandler={() => setShowPublishDialog(false)}
          submitHandler={handlePublishQuiz}
          submitButtonText="Publish"
        />
      )}
    </div>
  );
};

export default ManageQuiz;
